import type { OrganizationSettingsData } from '../../api/accountsApi';
import {
  isOnboardingEligible,
  isOnboardingIncomplete,
} from '../../hooks/useOnboarding';
import type { OnboardingWizardStep } from './OnboardingWizard.types';
import { ONBOARDING_STEPPER_STEPS } from './OnboardingWizard.types';

export type OnboardingGateSettings = Pick<
  OrganizationSettingsData,
  | 'onboardingEligible'
  | 'onboardingStatus'
  | 'onboardingSkipType'
  | 'onboardingAssignedToUserId'
>;

export function toOnboardingGateSettings(
  settings: OrganizationSettingsData | null | undefined,
): OnboardingGateSettings | null {
  if (!settings) {
    return null;
  }
  return {
    onboardingEligible: settings.onboardingEligible,
    onboardingStatus: settings.onboardingStatus,
    onboardingSkipType: settings.onboardingSkipType,
    onboardingAssignedToUserId: settings.onboardingAssignedToUserId,
  };
}

export type OrganizationAppAccountType =
  | 'compliance_education'
  | 'legacy'
  | 'trial'
  | null
  | undefined;

/**
 * Whether to load org settings and evaluate mandatory onboarding for this session.
 * Uses the API value only (no "default to legacy") so we still fetch settings when
 * appAccountType is momentarily missing from profile.
 */
export function isOnboardingWizardGateEnabled(
  orgId: string | undefined,
  isProfileLoading: boolean,
  organizationAppAccountType: OrganizationAppAccountType,
): boolean {
  if (isProfileLoading || !orgId) {
    return false;
  }
  if (organizationAppAccountType === 'legacy' || organizationAppAccountType === 'trial') {
    return false;
  }
  return true;
}

export interface ShouldAutoOpenOnboardingInput {
  settings: OnboardingGateSettings | null | undefined;
  userId: string | undefined;
  appAccountType?: OrganizationAppAccountType;
  /** Set when user arrived from a validated reassign email deep link. */
  openFromInvite?: boolean;
}

/**
 * Whether the onboarding wizard should open automatically after login.
 *
 * - First-time incomplete orgs: open (mandatory setup).
 * - deferred_later: do not auto-open (Courses banner handles return visits).
 * - reassigned: only the assigned colleague auto-opens.
 * - completed / ineligible: never auto-open.
 */
export function shouldAutoOpenOnboardingWizard({
  settings,
  userId,
  appAccountType,
  openFromInvite = false,
}: ShouldAutoOpenOnboardingInput): boolean {
  if (!settings || !isOnboardingEligible(settings.onboardingEligible, appAccountType ?? undefined)) {
    return false;
  }

  if (!isOnboardingIncomplete(settings.onboardingStatus)) {
    return false;
  }

  if (openFromInvite) {
    return true;
  }

  const skipType = settings.onboardingSkipType ?? null;

  if (skipType === 'deferred_later') {
    return false;
  }

  if (skipType === 'reassigned') {
    if (!userId || !settings.onboardingAssignedToUserId) {
      return false;
    }
    return settings.onboardingAssignedToUserId === userId;
  }

  // First-time mandatory setup (no skip yet).
  return true;
}

export interface CanVoluntarilyReopenOnboardingInput {
  settings: OnboardingGateSettings | null | undefined;
  appAccountType?: OrganizationAppAccountType;
}

/**
 * Whether the Courses banner may reopen onboarding after "I'll do this later".
 */
export function canVoluntarilyReopenOnboardingWizard({
  settings,
  appAccountType,
}: CanVoluntarilyReopenOnboardingInput): boolean {
  if (!settings || !isOnboardingEligible(settings.onboardingEligible, appAccountType ?? undefined)) {
    return false;
  }

  return (
    isOnboardingIncomplete(settings.onboardingStatus) &&
    settings.onboardingSkipType === 'deferred_later'
  );
}

export interface IsSettingsCustomizationBlockedInput {
  settings: OnboardingGateSettings | null | undefined;
  appAccountType?: OrganizationAppAccountType;
}

/**
 * Settings page brand identity CRUD is blocked until onboarding completes
 * for Option C eligible compliance_education orgs.
 *
 * Demo allowlist bypass applies on the API (PUT) only — UI always matches deployed
 * dev behaviour so localhost and tenant hosts stay consistent.
 */
export function isSettingsCustomizationBlocked({
  settings,
  appAccountType,
}: IsSettingsCustomizationBlockedInput): boolean {
  if (!settings || !isOnboardingEligible(settings.onboardingEligible, appAccountType ?? undefined)) {
    return false;
  }

  return isOnboardingIncomplete(settings.onboardingStatus);
}

// ——— AI onboarding step flow (CEE-992) ———

export type OnboardingAiFlowMode = 'with_ai' | 'without_ai';

export function resolveOnboardingAiFlowMode(
  aiRecommendationEnabled: boolean | null | undefined,
): OnboardingAiFlowMode {
  return aiRecommendationEnabled === true ? 'with_ai' : 'without_ai';
}

/** Next wizard step after the user continues from `current`. */
export function getOnboardingNextStep(
  current: OnboardingWizardStep,
  mode: OnboardingAiFlowMode,
): OnboardingWizardStep | null {
  if (current >= 6) {
    return null;
  }

  if (mode === 'with_ai') {
    return (current + 1) as OnboardingWizardStep;
  }

  if (current === 1) {
    return 3;
  }
  if (current === 3) {
    return 6;
  }
  return null;
}

/** Previous wizard step when the user taps Back. */
export function getOnboardingPreviousStep(
  current: OnboardingWizardStep,
  mode: OnboardingAiFlowMode,
): OnboardingWizardStep | null {
  if (current <= 1) {
    return null;
  }

  if (mode === 'with_ai') {
    return (current - 1) as OnboardingWizardStep;
  }

  if (current === 6) {
    return 3;
  }
  if (current === 3) {
    return 1;
  }

  return (current - 1) as OnboardingWizardStep;
}

/** Dot stepper position for the active internal wizard step. */
export function getOnboardingStepperProgress(
  activeStep: OnboardingWizardStep,
  mode: OnboardingAiFlowMode,
): { current: number; total: number } {
  if (activeStep === 1) {
    return { current: 1, total: ONBOARDING_STEPPER_STEPS };
  }

  if (mode === 'with_ai') {
    return {
      current: Math.min(activeStep, 6),
      total: 6,
    };
  }

  const withoutAiDot: Partial<Record<OnboardingWizardStep, number>> = {
    1: 1,
    3: 2,
    6: 3,
    7: 3,
  };

  return {
    current: withoutAiDot[activeStep] ?? 1,
    total: 3,
  };
}

/** Clears training-recommendation fields when the user opts out of AI. */
export function clearOnboardingAiTrainingFields<
  T extends {
    trainingPackage: string;
    trainingTopics: unknown[];
    priorityMonths: string[];
    monthsToAvoid: string[];
    additionalContext: string;
  },
>(form: T): T {
  return {
    ...form,
    trainingPackage: '',
    trainingTopics: [],
    priorityMonths: [],
    monthsToAvoid: [],
    additionalContext: '',
  };
}
